import { useState } from 'react';
import type { ReactNode } from 'react';

import AppMenu, { type AppMenuItem } from './AppMenu';
import AppWorkflowEditor from './AppWorkflowEditor';
import type { AppPlan } from './appPlans';
import { IconCheck, IconCopy, IconIssues, IconMore, IconPlus, IconTrash } from './appIcons';
import {
  WORKFLOW_PHASES,
  createWorkflow,
  type AppWorkflow,
  type AppWorkflowStore,
  type AppWorkflowTypeId
} from './appWorkflowStore';
import { formatCadence, getWorkflowCadence } from './appWorkflowUsage';

interface OpenEditor {
  workflow: AppWorkflow;
  isNew: boolean;
}

interface OpenMenu {
  anchor: HTMLElement;
  items: AppMenuItem[];
}

const TYPE_ICONS: Record<AppWorkflowTypeId, ReactNode> = {
  'security-audit': <IconIssues aria-hidden={true} />
};

const formatEdited = (updatedAt: string): string => {
  return new Date(updatedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

/** Workflow library: the saved workflows, and the way to start a new one. */
function AppWorkflowsView({
  plan,
  isSignedIn,
  workflowStore
}: {
  plan: AppPlan;
  isSignedIn: boolean;
  workflowStore: AppWorkflowStore;
}) {
  const {
    workflows,
    isLoading,
    error,
    saveWorkflow,
    duplicateWorkflow,
    activateWorkflow,
    deleteWorkflow
  } = workflowStore;
  const [editor, setEditor] = useState<OpenEditor | null>(null);
  const [openMenu, setOpenMenu] = useState<OpenMenu | null>(null);

  if (editor) {
    return (
      <AppWorkflowEditor
        key={editor.workflow.id}
        workflow={editor.workflow}
        isNew={editor.isNew}
        plan={plan}
        onSave={saveWorkflow}
        onBack={() => setEditor(null)}
      />
    );
  }

  const startWorkflow = (typeId: AppWorkflowTypeId) => setEditor({ workflow: createWorkflow(typeId), isNew: true });

  return (
    <div className="app-scroll">
      <div className="app-wf-library">
        <div className="app-wf-library-head">
          <p>Define how Havu assesses your applications: recon, exploit development and validation, each with its own tools and runs.</p>
          <button
            type="button"
            className="app-plan-run primary app-wf-new"
            disabled={!isSignedIn || plan.id === 'none'}
            onClick={() => startWorkflow('security-audit')}
          >
            <IconPlus aria-hidden={true} />
            New workflow
          </button>
        </div>

        <section className="app-tool-group" aria-labelledby="app-wf-saved">
          <h2 id="app-wf-saved">Your workflows</h2>
          {!isSignedIn ? (
            <p className="app-wf-empty">Sign in to customize the default assessment workflow.</p>
          ) : plan.id === 'none' ? (
            <p className="app-wf-empty">Choose a subscription before creating security-assessment workflows.</p>
          ) : isLoading ? (
            <p className="app-wf-empty">Loading workflows...</p>
          ) : workflows.length === 0 ? (
            <p className="app-wf-empty">
              No custom workflows. Pentests use the default six-run Recon, Exploit development, and Validation workflow.
            </p>
          ) : (
            <ul className="app-wf-list">
              {workflows.map((workflow) => {
                const cadence = getWorkflowCadence(workflow, plan.id);

                return (
                  <li
                    key={workflow.id}
                    className={`app-wf-item${workflow.isActive ? ' active' : ''}${openMenu?.anchor.dataset.workflowId === workflow.id ? ' menu-open' : ''}`}
                  >
                    <button
                      type="button"
                      className="app-wf-item-main"
                      onClick={() => setEditor({ workflow, isNew: false })}
                    >
                      <span className="app-wf-icon">{TYPE_ICONS[workflow.type]}</span>
                      <span className="app-wf-item-copy">
                        <span className="app-wf-item-title">
                          <strong>{workflow.name}</strong>
                          {workflow.isActive && (
                            <span className="app-status-pill active" title="Used when you run a pentest">Active</span>
                          )}
                        </span>
                        <span>
                          {workflow.description || workflow.phases.map((phase) => WORKFLOW_PHASES[phase.id].title).join(' → ')}
                        </span>
                      </span>
                      <span className="app-wf-item-meta">
                        <span className={cadence.count === 0 ? 'app-wf-over' : undefined}>{formatCadence(cadence)}</span>
                        {' · '}
                        {formatEdited(workflow.updatedAt)}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="app-row-action"
                      aria-label={`Options for ${workflow.name}`}
                      aria-haspopup="menu"
                      data-workflow-id={workflow.id}
                      onClick={(event) => {
                        const anchor = event.currentTarget;

                        setOpenMenu((current) => current?.anchor === anchor ? null : {
                          anchor,
                          items: [
                            ...(workflow.isActive ? [] : [{
                              label: 'Use for pentests',
                              icon: <IconCheck aria-hidden={true} />,
                              onSelect: () => void activateWorkflow(workflow.id)
                            }]),
                            {
                              label: 'Duplicate',
                              icon: <IconCopy aria-hidden={true} />,
                              onSelect: () => void duplicateWorkflow(workflow)
                            },
                            {
                              label: 'Delete',
                              icon: <IconTrash aria-hidden={true} />,
                              tone: 'danger',
                              onSelect: () => {
                                if (window.confirm(`Delete ${workflow.name}?`)) {
                                  void deleteWorkflow(workflow.id);
                                }
                              }
                            }
                          ]
                        });
                      }}
                    >
                      <IconMore aria-hidden={true} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {error && <p className="app-widget-error" role="alert">{error}</p>}
        </section>
      </div>

      {openMenu && (
        <AppMenu anchor={openMenu.anchor} items={openMenu.items} onClose={() => setOpenMenu(null)} />
      )}
    </div>
  );
}

export default AppWorkflowsView;
